'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  service: string;
  message: string;
  consent: boolean;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    service: '',
    message: '',
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1a1a1a',
    outline: 'none',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    boxSizing: 'border-box' as const,
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>
      <Navbar />

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #07182a 0%, #0a1d2f 50%, #0d2640 100%)',
        paddingTop: '140px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34,197,94,0.07) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div style={{ maxWidth: '90%', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: '0 0 20px' }}>
            Let&apos;s Secure Your <span style={{ color: '#22c55e' }}>Digital Assets</span>
          </h1>
          <p style={{ fontSize: '18px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto', lineHeight: '1.7' }}>
            Book a 30-minute demo or get a free security assessment. Our experts will show you exactly where you stand.
          </p>
        </div>
      </section>

      {/* Contact section */}
      <section style={{ background: '#f9fafb', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '60px', alignItems: 'start' }}>
          {/* Left: Info */}
          <div>
            <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 24px' }}>Get in Touch</h2>
            <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.7', margin: '0 0 40px' }}>
              Whether you need a penetration test, compliance audit, or strategic security guidance, our team is ready to help. Fill out the form and we&apos;ll get back to you within 24 hours.
            </p>

            {/* Contact details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '40px' }}>
              {[
                { icon: '📍', title: 'Head Office', lines: ['44-49, Prestige Atlanta', 'Koramangala 3 Block, Koramangala', 'Bengaluru, Karnataka 560034'] },
                { icon: '📧', title: 'Email Us', lines: ['validations@techlabs.works', 'info@techlabs.works'] },
                { icon: '📞', title: 'Call Us', lines: ['+91 88945 86775', 'Mon-Fri, 9AM-6PM IST'] },
              ].map((item) => (
                <div key={item.title} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ width: '44px', height: '44px', background: '#dcfce7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 4px' }}>{item.title}</h3>
                    {item.lines.map((line) => (
                      <p key={line} style={{ fontSize: '13px', color: '#6b7280', margin: '2px 0' }}>{line}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Why contact us */}
            <div style={{ background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', borderRadius: '16px', padding: '28px', border: '1px solid rgba(34,197,94,0.2)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#22c55e', margin: '0 0 16px' }}>What Happens Next?</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { step: '1', text: 'We review your request within 24 hours' },
                  { step: '2', text: 'A senior security expert contacts you' },
                  { step: '3', text: 'We conduct a free 30-min scoping call' },
                  { step: '4', text: 'You receive a tailored proposal within 48 hours' },
                ].map((item) => (
                  <div key={item.step} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ width: '28px', height: '28px', background: '#22c55e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{item.step}</div>
                    <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
                <h3 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 12px' }}>Message Sent!</h3>
                <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.6' }}>
                  Thank you for reaching out. Our security experts will contact you within 24 hours to discuss your requirements.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 28px' }}>Book a Demo / Get a Quote</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="John"
                      style={inputStyle}
                      className="focus:border-green-400 focus:ring-2 focus:ring-green-100"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Doe"
                      style={inputStyle}
                      className="focus:border-green-400 focus:ring-2 focus:ring-green-100"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Work Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    style={inputStyle}
                    className="focus:border-green-400 focus:ring-2 focus:ring-green-100"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      style={inputStyle}
                      className="focus:border-green-400"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Company *</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Acme Corp"
                      style={inputStyle}
                      className="focus:border-green-400"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Job Title</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                    placeholder="CISO, Security Manager, etc."
                    style={inputStyle}
                    className="focus:border-green-400"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Service Interested In *</label>
                  <select
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    className="focus:border-green-400"
                  >
                    <option value="">Select a service...</option>
                    <option value="web-app">Web Application Pentesting</option>
                    <option value="mobile">Mobile App Security Testing</option>
                    <option value="api">API Security Testing</option>
                    <option value="network">Network Penetration Testing</option>
                    <option value="cloud">Cloud Security Assessment</option>
                    <option value="red-team">Red Team Operations</option>
                    <option value="compliance">Compliance Audit</option>
                    <option value="vciso">vCISO Services</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your security requirements, current challenges, or any specific questions..."
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical' as const }}
                    className="focus:border-green-400"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '24px' }}>
                  <input
                    type="checkbox"
                    id="consent"
                    checked={formData.consent}
                    onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                    style={{ marginTop: '2px', accentColor: '#22c55e', width: '16px', height: '16px', flexShrink: 0 }}
                  />
                  <label htmlFor="consent" style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', cursor: 'pointer' }}>
                    I agree to TechLabs&apos; Privacy Policy and consent to being contacted about cybersecurity services. We never share your data with third parties.
                  </label>
                </div>
                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '100px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'background 0.18s, box-shadow 0.18s',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  className="hover:bg-green-600 hover:shadow-lg"
                >
                  Book a Demo →
                </button>
                <p style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginTop: '12px' }}>
                  🔒 Your information is encrypted and secure. We respond within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section style={{ background: '#fff', padding: '60px 0', borderTop: '1px solid #f3f4f6' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trusted by organizations worldwide</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '32px', alignItems: 'center' }}>
            {['Fortune 500 Bank', 'Global FinTech', 'HealthCare Corp', 'SaaS Leader', 'Telecom Giant', 'E-Commerce Co'].map((client) => (
              <div key={client} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
